import { formatDate, formatPatientName, NO_RESPONSIBLE_USER_PLACEHOLDER } from 'utils/format';
import { Order } from 'models/order';

type Translate = (key: string, options?: Record<string, unknown>) => string;

const escapeHtml = (value: string): string =>
    value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const workflowStepCell = (order: Order): string => {
    const currentStep = order.workFlow?.currentStep;
    const stepName = escapeHtml(currentStep?.name || '-');
    const departmentName = currentStep?.departmentName
        ? `<br/><small>${escapeHtml(currentStep.departmentName)}</small>`
        : '';
    return `${stepName}${departmentName}`;
};

const buildRow = (order: Order, cameInViaLabel: string, translate: Translate): string => `
    <tr>
        <td>${escapeHtml(order.internalOrderId ? `${order.orderId} / ${order.internalOrderId}` : order.orderId)}</td>
        <td>${escapeHtml(formatPatientName(order.patient, translate('orders:NO_PATIENT_REFERENCE')))}</td>
        <td>${escapeHtml(order.deliveryDetails?.destination?.name || '-')}</td>
        <td>${escapeHtml(order.dentist?.fullName || NO_RESPONSIBLE_USER_PLACEHOLDER)}</td>
        <td>${order.deliveryDetails?.date ? escapeHtml(formatDate(new Date(order.deliveryDetails.date), 'date')) : '-'}</td>
        <td>${workflowStepCell(order)}</td>
        <td>${escapeHtml((order.restorationTypes || []).join(', ') || '-')}</td>
        <td>${escapeHtml(cameInViaLabel)}</td>
    </tr>`;

/** Client-side print of the click-through order list (print-orders-list.ts pattern). */
export const printProductionOverviewOrders = (
    orders: Order[],
    cameInViaLabelFor: (order: Order) => string,
    listTitle: string,
    translate: Translate,
): void => {
    const headers = [
        translate('common:ORDER_ID'),
        translate('common:PATIENT'),
        translate('delivery-schedule:DELIVERY_TO'),
        translate('common:DENTIST'),
        translate('orders:DELIVERY_DATE'),
        translate('orders:WORKFLOW_STEP'),
        translate('orders:CONSTRUCTIONS'),
        translate('production-overview:CAME_IN_VIA'),
    ]
        .map((header) => `<th>${escapeHtml(header)}</th>`)
        .join('');

    const title = escapeHtml(listTitle);
    const html = `<!DOCTYPE html>
<html>
<head>
    <title>${title}</title>
    <style>
        body { font-family: Arial, Helvetica, sans-serif; font-size: 12px; margin: 24px; }
        h1 { font-size: 16px; margin-bottom: 4px; }
        table { width: 100%; border-collapse: collapse; margin-top: 12px; }
        th, td { border: 1px solid #ccc; padding: 4px 8px; text-align: left; vertical-align: top; }
        th { background: #f0f0f0; }
        small { color: #555; }
    </style>
</head>
<body>
    <h1>${title}</h1>
    <table>
        <thead><tr>${headers}</tr></thead>
        <tbody>${orders.map((order) => buildRow(order, cameInViaLabelFor(order), translate)).join('')}</tbody>
    </table>
</body>
</html>`;

    const printWindow = window.open('', 'PRINT', 'height=800,width=1000');
    if (!printWindow) return;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
};
